import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface StatusCodeDto {
  statusCode: number;
  statusType: number;
  lng_1: string;
  lng_2?: string;
}

class StatusCodesService {
  private cache: Map<number, StatusCodeDto[]> = new Map();

  /**
   * Gets the current company number from the authenticated user's token
   * @returns Company number
   * @throws Error if no company data is available
   */
  private getCompanyNo(): string {
    const userData = AuthService.getUserDataFromToken();
    if (!userData?.companyNo) {
      throw new Error('No company data available');
    }
    return userData.companyNo;
  }

  /**
   * Get status codes for a specific status type
   * @param statusType Status type to retrieve (e.g., 27 for transaction types)
   * @returns Promise with array of status code DTOs
   */
  async getByStatusType(statusType: number): Promise<StatusCodeDto[]> {
    // Check cache first
    if (this.cache.has(statusType)) {
      return this.cache.get(statusType)!;
    }

    const companyNo = this.getCompanyNo();
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/statuscodes?statusType=${statusType}`
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get status codes: ${response.status} ${response.statusText}. ${errorText}`);
    }

    const statusCodes: StatusCodeDto[] = await response.json();

    // Cache the result
    this.cache.set(statusType, statusCodes);

    return statusCodes;
  }

  /**
   * Get a specific status code by its value and type
   * @param statusCode Status code value
   * @param statusType Status type
   * @returns Promise with status code DTO or null if not found
   */
  async getByCodeAndType(statusCode: number, statusType: number): Promise<StatusCodeDto | null> {
    // Try to get from cache first
    const cachedList = this.cache.get(statusType);
    if (cachedList) {
      const found = cachedList.find(sc => sc.statusCode === statusCode);
      if (found) return found;
    }

    const companyNo = this.getCompanyNo();
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/statuscodes/${statusCode}?statusType=${statusType}`
    );

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      const errorText = await response.text();
      throw new Error(`Failed to get status code: ${response.status} ${response.statusText}. ${errorText}`);
    }

    return await response.json();
  }

  /**
   * Clear the cache for a specific status type or all types
   * @param statusType Optional status type to clear. If not provided, clears all cache.
   */
  clearCache(statusType?: number): void {
    if (statusType !== undefined) {
      this.cache.delete(statusType);
    } else {
      this.cache.clear();
    }
  }
}

export const statusCodesService = new StatusCodesService();
